import { PASSWORD_MIN_LENGTH, type CreateUserInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Select } from 'antd';
import { Lock, Mail, Phone, User } from 'lucide-react';
import type { RoleWithPermissions } from '../roles/roles.api';

interface CreateUserFormValues {
  name: string;
  email: string;
  phone?: string;
  password: string;
  roles?: string[];
}

interface CreateUserDrawerProps {
  open: boolean;
  roleOptions: RoleWithPermissions[];
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateUserInput) => Promise<boolean>;
  onClose: () => void;
}

/** Name, email, phone, an initial password, and roles to start with — role changes after creation go through the table's own row action. */
export function CreateUserDrawer({ open, roleOptions, error, submitting, onCreate, onClose }: CreateUserDrawerProps) {
  const [form] = Form.useForm<CreateUserFormValues>();

  async function submit(values: CreateUserFormValues) {
    const created = await onCreate({ ...values, phone: values.phone || undefined, roles: values.roles });
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create user" placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateUserFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input prefix={<User className="size-4 text-muted" />} autoFocus />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input prefix={<Mail className="size-4 text-muted" />} autoComplete="email" />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input prefix={<Phone className="size-4 text-muted" />} />
        </Form.Item>
        <Form.Item
          name="password"
          label="Initial password"
          rules={[{ required: true, min: PASSWORD_MIN_LENGTH, message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters` }]}
        >
          <Input.Password prefix={<Lock className="size-4 text-muted" />} autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="roles" label="Roles">
          <Select mode="multiple" placeholder="No roles" options={roleOptions.map((role) => ({ value: role.key, label: role.name }))} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create user
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
